import React, { useState, useEffect } from "react";
import { NavLink, withRouter, useHistory } from "react-router-dom";
import { notification, Spin } from "antd";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Cookies from "js-cookie";
import moment from "moment";
import { 
  HiOutlineChevronLeft, 
  HiOutlineUser, 
  HiOutlinePhone, 
  HiOutlineMail, 
  HiOutlineLocationMarker,
  HiOutlineOfficeBuilding,
  HiOutlineCamera,
  HiOutlineCheckCircle
} from "react-icons/hi";

import "./ProfileComp.css";
import axiosInstance from "../../api/axiosInstance";
import { initEncryptionSession } from "../../encryptionSession";
import { encryptData, decryptData } from "../Crypto_Helpers/cryptoHelper";

function ProfileComp(props) {
  const history = useHistory();
  const { t, i18n } = props;

  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [profileData, setProfileData] = useState(null);

  const [pharmacyName, setPharmacyName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [location, setLocation] = useState("");
  const [address, setAddress] = useState("");
  const [clinicName, setClinicName] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [imageChanged, setImageChanged] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);

  useEffect(() => {
    getProfileData();
  }, []);

  const getProfileData = async () => {
    setLoading(true);
    try {
      const pharmId =
        Cookies.get("pharmacy_id") ||
        Cookies.get("vendorId") ||
        localStorage.getItem("pharmacy_id") ||
        localStorage.getItem("vendorId") ||
        "0";

      const aesKey = await initEncryptionSession();
      const payload = {
        pharm_id: pharmId,
      };

      let requestData = payload;
      if (aesKey) {
        try {
          const encrypted = await encryptData(payload, aesKey);
          requestData = { objects: encrypted };
        } catch (e) {
          requestData = payload;
        }
      }

      const response = await axiosInstance.post("pharmacy/getPharmProfile", requestData);

      let dataList = [];
      if (response && response.data) {
        if (Array.isArray(response.data.data)) {
          dataList = response.data.data;
        } else if (typeof response.data.data === "string" && aesKey) {
          try {
            dataList = await decryptData(response.data.data, aesKey);
          } catch (e) {
            dataList = [];
          }
        } else if (Array.isArray(response.data)) {
          dataList = response.data;
        }
      }

      if (Array.isArray(dataList) && dataList.length > 0) {
        const profile = dataList[0];
        setProfileData(profile);
        setPharmacyName(profile.vendor_name || profile.contact_person || profile.pharm_name || "");
        setEmail(profile.vendor_email || profile.person_email || profile.email || "");
        setPhone(profile.vendor_phone || profile.vendor_contact_mobile || profile.mobile || profile.phone || "");
        setLocation(profile.vendor_location || profile.location || profile.city || "");
        setAddress(profile.vendor_address || profile.address || "");
        setClinicName(profile.clinic_name || "");
        setImageUrl(profile.vendor_filename || profile.vendor_profile_path || "");

        if (profile.vendor_name) {
          localStorage.setItem("pharmacy_name", profile.vendor_name);
          localStorage.setItem("User_name", profile.vendor_name);
          Cookies.set("User_name", profile.vendor_name);
        }
        if (profile.clinic_name) {
          localStorage.setItem("clinic_name", profile.clinic_name);
        }
        if (profile.vendor_address) {
          localStorage.setItem("pharmacy_address", profile.vendor_address);
        }
        if (profile.vendor_phone || profile.vendor_contact_mobile) {
          localStorage.setItem("pharmacy_phone", profile.vendor_phone || profile.vendor_contact_mobile);
        }
        if (profile.vendor_email || profile.person_email) {
          localStorage.setItem("pharmacy_email", profile.vendor_email || profile.person_email);
        }
        const logoUrl = profile.vendor_profile_path || profile.vendor_filename || "";
        if (logoUrl) {
          Cookies.set("profilePath", logoUrl);
          localStorage.setItem("profilePath", logoUrl);
        }
      }
    } catch (err) {
      console.error("Failed to load pharmacy profile:", err);
      notification.error({ message: "Could not load profile details." });
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      notification.warning({ message: "Please upload an image file (JPEG/PNG)" });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      notification.warning({ message: "Image size must be less than 5MB" });
      return;
    }

    setUploadLoading(true);
    try {
      const formData = new FormData();
      formData.append("module_id", "9");
      formData.append("file", file);
      formData.append("pathLocation", "PROFILE/");

      const response = await axiosInstance.post("pharmacy/awsS3FileUpload", formData);
      let uploadedUrl = "";

      if (response.data && response.data.data) {
        uploadedUrl = response.data.data.url || response.data.data.Location || response.data.data;
      } else if (response.data && response.data.encryptedData) {
        const aesKey = await initEncryptionSession();
        const decrypted = await decryptData(response.data.encryptedData, aesKey);
        uploadedUrl = decrypted?.url || decrypted?.Location || decrypted;
      }

      if (uploadedUrl && typeof uploadedUrl === "string") {
        setImageUrl(uploadedUrl);
        setImageChanged(true);
        notification.success({ message: "Profile photo uploaded! Click 'Save Changes' to update profile." });
      }
    } catch (err) {
      console.error("Image upload failed:", err);
      notification.error({ message: "Failed to upload image. Please try again." });
    } finally {
      setUploadLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!phone.trim() || phone.replace(/\D/g, "").length < 7) {
      notification.error({ message: "Please enter a valid Contact Number (min 7 digits)" });
      return;
    }
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      notification.error({ message: "Please enter a valid email address" });
      return;
    }
    if (!address.trim()) {
      notification.error({ message: "Please enter Pharmacy Address" });
      return;
    }

    setSaveLoading(true);
    try {
      const pharmId =
        Cookies.get("pharmacy_id") ||
        Cookies.get("vendorId") ||
        localStorage.getItem("pharmacy_id") ||
        "0";

      const aesKey = await initEncryptionSession();
      const payload = {
        vendor_since: profileData?.vendor_since || "",
        address: address,
        mobile: phone,
        email: email,
        website: profileData?.vendor_website || "",
        contact_person: pharmacyName,
        modified_by: Cookies.get("vendorId") || Cookies.get("pharmacy_id") || localStorage.getItem("vendorId") || 0,
        pharm_id: pharmId,
        profile_image: imageChanged ? imageUrl : (profileData?.vendor_filename || profileData?.vendor_profile_path || ""),
      };

      let requestData = payload;
      if (aesKey) {
        try {
          const encrypted = await encryptData(payload, aesKey);
          requestData = { objects: encrypted };
        } catch (e) {
          requestData = payload;
        }
      }

      const response = await axiosInstance.put("pharmacy/editPharmProfile", requestData);

      if (response && response.data && (response.data.status === 1 || response.data.msg === "Success" || response.data.msg === "Profile updated Successfully.")) {
        setShowSuccessToast(true);
        setTimeout(() => setShowSuccessToast(false), 3000);
        getProfileData();
      } else {
        notification.error({ message: response?.data?.msg || "Failed to update profile." });
      }
    } catch (err) {
      console.error("Profile update error:", err);
      notification.error({ message: "An error occurred while updating profile." });
    } finally {
      setSaveLoading(false);
    }
  };

  const handleLogout = () => {
    Object.keys(Cookies.get()).forEach((c) => Cookies.remove(c));
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = "/";
  };

  return (
    <div className={`${i18n?.language === "ar" ? "arabic_cls" : ""} deal_listcreatead`}>
      <div className="profile_background min-h-screen flex flex-col bg-[#f8fafc] overflow-y-auto relative pb-20">
        
        {/* Header matching Clinic & Doctor Web */}
        <div className="flex-none p-4 pt-4">
          <div className="max-w-7xl mx-auto">
            <div className="bg-white p-3.5 sm:p-4 rounded-xl shadow-2xs border border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <NavLink
                  to="/Home/dashboard"
                  className="p-2 hover:bg-gray-50 rounded-lg text-gray-400 transition-all active:scale-95 flex items-center justify-center border border-transparent hover:border-gray-100"
                >
                  <HiOutlineChevronLeft size={20} />
                </NavLink>
                <div>
                  <h2 className="text-base sm:text-lg font-bold text-[#1e293b] tracking-tight leading-none mb-1">
                    Pharmacy <span className="text-teal-600">Profile</span>
                  </h2>
                  <p className="text-gray-400 text-xs font-medium italic">
                    Manage your pharmacy vendor credentials, contacts, and preferences
                  </p>
                </div>
              </div>

              <Button
                className="px-4 py-1.5 bg-red-50 text-red-600 font-bold text-xs rounded-lg hover:bg-red-600 hover:text-white transition-all duration-300 border border-red-100/50 shadow-2xs self-start sm:self-auto"
                variant="contained"
                onClick={handleLogout}
              >
                {t ? t("Sign Out") : "Sign Out"}
              </Button>
            </div>
          </div>
        </div>

        {/* Main Content Body */}
        {loading ? (
          <div className="flex items-center justify-center h-96">
            <Spin size="large" tip="Loading profile details..." />
          </div>
        ) : (
          <div className="px-4 sm:px-5">
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
                
                {/* Left Column: Avatar & Quick Status */}
                <div className="lg:col-span-1 space-y-4">
                  <div className="bg-white p-5 rounded-xl shadow-2xs border border-gray-100 flex flex-col items-center text-center">
                    <div className="relative group mb-4">
                      <div className="w-28 h-28 rounded-2xl overflow-hidden border-3 border-teal-50 shadow-md transition-transform duration-500 group-hover:scale-105 bg-white flex items-center justify-center p-1.5">
                        {uploadLoading ? (
                          <Spin size="default" />
                        ) : imageUrl ? (
                          <img
                            src={imageUrl}
                            className="w-full h-full object-contain"
                            alt="Pharmacy Avatar"
                          />
                        ) : (
                          <div className="flex flex-col items-center justify-center text-gray-300">
                            <HiOutlineUser size={36} />
                            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mt-1">No Photo</span>
                          </div>
                        )}
                      </div>

                      <label
                        htmlFor="profile-upload"
                        className="absolute -bottom-1 -right-1 w-8 h-8 bg-gray-900 text-white rounded-lg flex items-center justify-center cursor-pointer shadow-md hover:bg-teal-600 transition-all active:scale-90"
                      >
                        <HiOutlineCamera size={15} />
                      </label>
                      <input
                        id="profile-upload"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleImageUpload}
                      />
                    </div>

                    <h3 className="text-base font-bold text-[#1e293b] tracking-tight mb-1.5">
                      {pharmacyName || "Pharmacy Vendor"}
                    </h3>
                    <div className="flex items-center gap-1.5 bg-teal-50 px-3 py-1 rounded-full border border-teal-100/60">
                      <div className="w-2 h-2 rounded-full bg-teal-500 animate-pulse" />
                      <span className="text-[10px] font-bold text-teal-700 uppercase tracking-widest">
                        Pharmacy Vendor
                      </span>
                    </div>

                    {clinicName && (
                      <div className="mt-3 pt-3 border-t border-gray-100 w-full flex items-center justify-center gap-1.5 text-xs font-semibold text-gray-500">
                        <HiOutlineOfficeBuilding className="text-teal-600" size={15} />
                        <span>Attached to: <strong className="text-gray-800">{clinicName}</strong></span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Right Column: Profile Form Fields */}
                <div className="lg:col-span-2 space-y-4">
                  <div className="bg-white p-5 rounded-xl shadow-2xs border border-gray-100">
                    <div className="flex items-center justify-between mb-5 pb-3 border-b border-gray-50">
                      <h4 className="text-base font-bold text-[#1e293b] tracking-tight flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-lg bg-teal-50 text-teal-600 flex items-center justify-center shadow-inner border border-teal-100/50">
                          <HiOutlineUser size={16} />
                        </div>
                        Pharmacy Credentials & Details
                      </h4>
                      <div className="px-3 py-1 bg-gray-50 rounded-full border border-gray-100 italic text-gray-400 text-[10px] font-semibold">
                        Last synced: {moment().format("hh:mm A")}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                      
                      {/* Pharmacy Name */}
                      <div className="space-y-1 pharmacy-profile-readonly">
                        <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider ml-1 flex items-center gap-1">
                          Pharmacy Name <span className="text-red-500">*</span>
                        </label>
                        <TextField
                          fullWidth
                          variant="outlined"
                          value={pharmacyName}
                          InputProps={{
                            readOnly: true,
                            style: { borderRadius: "0.75rem", height: "42px", fontSize: "13px" }
                          }}
                        />
                      </div>

                      {/* Email Address */}
                      <div className="space-y-1 pharmacy-profile-readonly">
                        <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider ml-1 flex items-center gap-1">
                          Contact Email <span className="text-red-500">*</span>
                        </label>
                        <TextField
                          fullWidth
                          variant="outlined"
                          value={email}
                          placeholder="pharmacy@example.com"
                          InputProps={{
                            readOnly: true,
                            style: { borderRadius: "0.75rem", height: "42px", fontSize: "13px" }
                          }}
                        />
                      </div>

                      {/* Contact Phone */}
                      <div className="space-y-1 pharmacy-profile-input">
                        <label className="text-[11px] font-bold text-gray-700 uppercase tracking-wider ml-1 flex items-center gap-1">
                          Contact Phone <span className="text-red-500">*</span>
                        </label>
                        <TextField
                          fullWidth
                          variant="outlined"
                          value={phone}
                          placeholder="Enter contact mobile number"
                          onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                          InputProps={{
                            style: { borderRadius: "0.75rem", height: "42px", fontSize: "13px" }
                          }}
                        />
                      </div>

                      {/* Location / City */}
                      <div className="space-y-1 pharmacy-profile-readonly">
                        <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider ml-1 flex items-center gap-1">
                          Location / City <span className="text-red-500">*</span>
                        </label>
                        <TextField
                          fullWidth
                          variant="outlined"
                          value={location}
                          placeholder="Location / City"
                          InputProps={{
                            readOnly: true,
                            style: { borderRadius: "0.75rem", height: "42px", fontSize: "13px" }
                          }}
                        />
                      </div>

                      {/* Associated Clinic (if any) */}
                      {clinicName && (
                        <div className="md:col-span-2 space-y-1 pharmacy-profile-readonly">
                          <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider ml-1 flex items-center gap-1">
                            Associated Clinic
                          </label>
                          <TextField
                            fullWidth
                            variant="outlined"
                            value={clinicName}
                            InputProps={{
                              readOnly: true,
                              style: { borderRadius: "0.75rem", height: "42px", fontSize: "13px" }
                            }}
                          />
                        </div>
                      )}

                      {/* Full Pharmacy Address */}
                      <div className="md:col-span-2 space-y-1 pharmacy-profile-input">
                        <label className="text-[11px] font-bold text-gray-700 uppercase tracking-wider ml-1 flex items-center gap-1">
                          Full Pharmacy Address <span className="text-red-500">*</span>
                        </label>
                        <TextField
                          fullWidth
                          multiline
                          rows={2}
                          variant="outlined"
                          placeholder="Enter complete pharmacy address, building, street..."
                          value={address}
                          onChange={(e) => setAddress(e.target.value)}
                          InputProps={{
                            style: { borderRadius: "0.75rem", fontSize: "13px" }
                          }}
                        />
                      </div>

                    </div>

                    {/* Bottom Save & Note Section */}
                    <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-gray-100 pt-4">
                      <div className="flex items-center gap-2 text-amber-700 bg-amber-50 px-3.5 py-1.5 rounded-full border border-amber-200/60 text-center sm:text-left">
                        <i className="fa fa-info-circle text-xs text-amber-600" />
                        <span className="text-[10px] font-bold uppercase tracking-wider">
                          Note: Pharmacy name & email are managed by administrator
                        </span>
                      </div>

                      <button
                        type="button"
                        onClick={handleSaveProfile}
                        disabled={saveLoading}
                        className="w-full sm:w-auto justify-center px-5 py-2.5 bg-gray-900 hover:bg-teal-600 text-white font-bold text-xs uppercase tracking-wider rounded-xl transition-all shadow-md active:scale-95 flex items-center gap-2 group cursor-pointer disabled:opacity-50"
                      >
                        {saveLoading ? (
                          <>
                            <Spin size="small" />
                            <span>Saving...</span>
                          </>
                        ) : (
                          <>
                            <span>Save Changes</span>
                            <div className="w-1.5 h-1.5 rounded-full bg-teal-400 group-hover:animate-ping" />
                          </>
                        )}
                      </button>
                    </div>

                  </div>
                </div>

              </div>
            </div>
          </div>
        )}

        {/* Success Toast */}
        {showSuccessToast && (
          <div className="fixed bottom-10 right-10 z-[9999] animate-in">
            <div className="bg-[#1e293b] text-white px-8 py-5 rounded-[2rem] shadow-2xl flex items-center gap-6 border border-white/10">
              <div className="w-12 h-12 bg-teal-500/20 rounded-2xl flex items-center justify-center border border-teal-500/30">
                <HiOutlineCheckCircle className="text-teal-400 text-2xl" />
              </div>
              <div>
                <p className="text-sm font-black tracking-tight uppercase leading-none mb-1">
                  Profile Updated
                </p>
                <p className="text-xs text-gray-400 font-medium italic">
                  Pharmacy details updated successfully!
                </p>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

export default withRouter(ProfileComp);
